import React from 'react';
import { TableCell } from '@material-ui/core';
import { InventoryReportSnapshotItemRow } from './types';
import { COLUMNS } from './columnConfig';
import {
    DescriptionCell,
    SupplierCell,
    LocationCell,
    OrderQtyCell,
    NumericCell,
    ReceivedLastCell,
} from './cells';

const CELL_STYLE: React.CSSProperties = {
    padding: '2px 4px',
    borderRight: '1px solid #e0e0e0',
    boxSizing: 'border-box',
    verticalAlign: 'middle',
};

const STICKY_CELL_STYLE: React.CSSProperties = {
    ...CELL_STYLE,
    position: 'sticky',
    zIndex: 9,
    backgroundColor: '#fff',
    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
};

export interface RowHandlers {
    onUpdate: (newValue: number, row: InventoryReportSnapshotItemRow) => Promise<any>;
    onCommitAndNavigate: (newValue: number, oldData: InventoryReportSnapshotItemRow, direction: 'next' | 'prev') => Promise<void>;
}

export interface MemoizedTableRowProps {
    row: InventoryReportSnapshotItemRow;
    editable: boolean;
    unsavedChange?: { originalValue: number; newValue: number } | null;
    handlers: RowHandlers;
    errorIconClassName?: string;
    focusRowId?: string | null;
    savingRowId?: string | null;
}

function formatReceivedLast(row: InventoryReportSnapshotItemRow): { date: string; qty: string } {
    if (!row.receivedLastDate) return { date: '', qty: '' };
    const date = typeof row.receivedLastDate === 'string'
        ? row.receivedLastDate.split('T')[0]
        : row.receivedLastDate;
    return {
        date,
        qty: String(row.receivedLastTime ?? 0),
    };
}

const MemoizedTableRow: React.FC<MemoizedTableRowProps> = ({
    row,
    editable,
    unsavedChange,
    handlers,
    errorIconClassName,
    focusRowId,
    savingRowId,
}) => {
    return (
        <>
            {COLUMNS.map((col) => {
                const baseStyle = col.sticky
                    ? { ...STICKY_CELL_STYLE, left: col.stickyLeft }
                    : CELL_STYLE;
                const cellStyle: React.CSSProperties = {
                    ...baseStyle,
                    width: col.width,
                    minWidth: col.minWidth,
                    maxWidth: col.width,
                    textAlign: col.align,
                };

                let content: React.ReactNode = null;
                switch (col.id) {
                    case 'description':
                        content = (
                            <DescriptionCell
                                description={row.description}
                                validationErrors={row.validationErrors}
                                errorIconClassName={errorIconClassName}
                            />
                        );
                        break;
                    case 'supplier':
                        content = (
                            <SupplierCell
                                supplier={row.supplier}
                                noSupplier={row.noSupplier}
                            />
                        );
                        break;
                    case 'location':
                        content = <LocationCell location={row.location} />;
                        break;
                    case 'onHandQuantity':
                    case 'reorderLevel':
                    case 'maxOrderLevel':
                    case 'quantityInCase':
                    case 'sales30Days':
                    case 'sales60Days':
                    case 'sales90Days':
                    case 'sales180Days':
                    case 'onOrder':
                        content = (
                            <NumericCell
                                value={(row as any)[col.id]}
                                format="number"
                            />
                        );
                        break;
                    case 'orderQtyToPlace':
                        content = (
                            <OrderQtyCell
                                value={row.orderQtyToPlace}
                                editable={editable && !row.noSupplier}
                                unsavedChange={unsavedChange}
                                rowId={row.id}
                                row={row}
                                onUpdate={handlers.onUpdate}
                                onCommitAndNavigate={handlers.onCommitAndNavigate}
                                focusRowId={focusRowId}
                                savingRowId={savingRowId}
                            />
                        );
                        break;
                    case 'anyShort':
                        // Show tick only for locations that actually have short stock (shortStock), not anyShort
                        content = row.shortStock ? '✓' : '';
                        break;
                    case 'receivedLast': {
                        const { date, qty } = formatReceivedLast(row);
                        content = <ReceivedLastCell date={date} qty={qty} />;
                        break;
                    }
                    case 'lastPurchasePrice':
                        content = (
                            <NumericCell
                                value={row.lastPurchasePrice}
                                format="currency"
                            />
                        );
                        break;
                    case 'retailPrice':
                        content = (
                            <NumericCell
                                value={row.retailPrice}
                                format="currency"
                            />
                        );
                        break;
                    case 'profitMargin':
                        content = (
                            <NumericCell
                                value={row.profitMargin}
                                format="percent"
                            />
                        );
                        break;
                    default:
                        content = null;
                }

                return (
                    <TableCell
                        key={col.id}
                        component="div"
                        style={cellStyle}
                        padding="none"
                    >
                        {content}
                    </TableCell>
                );
            })}
        </>
    );
};

function arePropsEqual(
    prev: MemoizedTableRowProps,
    next: MemoizedTableRowProps
): boolean {
    if (prev.row.id !== next.row.id) return false;
    if (prev.row.orderQtyToPlace !== next.row.orderQtyToPlace) return false;
    if (prev.editable !== next.editable) return false;
    if (prev.focusRowId !== next.focusRowId) return false;
    if (prev.savingRowId !== next.savingRowId) return false;
    const prevNew = prev.unsavedChange?.newValue;
    const nextNew = next.unsavedChange?.newValue;
    if (prevNew !== nextNew) return false;
    if (prev.row.description !== next.row.description) return false;
    if (prev.row.supplier !== next.row.supplier) return false;
    if (prev.row.noSupplier !== next.row.noSupplier) return false;
    if (prev.row.location !== next.row.location) return false;
    const prevErrLen = prev.row.validationErrors?.length ?? 0;
    const nextErrLen = next.row.validationErrors?.length ?? 0;
    if (prevErrLen !== nextErrLen) return false;
    return true;
}

export default React.memo(MemoizedTableRow, arePropsEqual);
